import { DiagramLogData, HttpStepLog } from "components/CC/atoms/diagramLogAtom";

import StepRunData = Paldock.LaravelConnectionCreator.Data.StepRunData;

const META_KEYS = new Set(["_step", "_system", "run_id", "step_run_id", "step_title"]);
const NON_BODY_KEYS = new Set(["headers", "query"]);

const tryParseJson = (value: unknown): unknown => {
    if (typeof value !== "string") return value;

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

export const collectLeafPaths = (value: unknown, currentPath: string[] = []): string[][] => {
    const normalizedValue = currentPath.length === 0 ? tryParseJson(value) : value;

    if (normalizedValue == null) {
        return currentPath.length > 0 ? [currentPath] : [];
    }

    if (Array.isArray(normalizedValue)) {
        return currentPath.length > 0 ? [currentPath] : [];
    }

    if (!isRecord(normalizedValue)) {
        return currentPath.length > 0 ? [currentPath] : [];
    }

    const entries = Object.entries(normalizedValue);
    if (entries.length === 0) {
        return currentPath.length > 0 ? [currentPath] : [];
    }

    return entries.flatMap(([key, nestedValue]) => collectLeafPaths(nestedValue, [...currentPath, key]));
};

const normalizePathSet = (paths: string[][]): string[][] => {
    return Array.from(new Set(paths.filter((path) => path.length > 0).map((path) => JSON.stringify(path))))
        .map((path) => JSON.parse(path) as string[])
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
};

const UUID_LIKE_KEY_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isNestedStepMetadataValue = (value: unknown): boolean => {
    if (!isRecord(value)) return false;

    const keys = Object.keys(value);
    return keys.length === 1 && keys[0] === "_step";
};

const sanitizeTopLevelDataOut = (dataOut: unknown): Record<string, unknown> | null => {
    if (!isRecord(dataOut)) return null;

    const sanitizedEntries = Object.entries(dataOut).filter(([key, value]) => {
        if (META_KEYS.has(key) || NON_BODY_KEYS.has(key)) return false;
        if (key.startsWith("_")) return false;
        if (UUID_LIKE_KEY_PATTERN.test(key)) return false;
        if (isNestedStepMetadataValue(value)) return false;
        return true;
    });

    if (sanitizedEntries.length === 0) return null;

    return Object.fromEntries(sanitizedEntries);
};

export const aggregateHttpStepData = (stepRuns?: StepRunData[] | null): DiagramLogData => {
    const byStepId = new Map<string, HttpStepLog>();

    (stepRuns ?? [])
        .filter(
            (stepRun) =>
                !!stepRun.type && ["http", "webhook", "scenario"].includes(stepRun.type) && (stepRun.run ?? 0) > 0
        )
        .forEach((stepRun) => {
            if (!stepRun.step_id) return;

            const current = byStepId.get(stepRun.step_id) ?? {
                stepId: stepRun.step_id,
                stepType: stepRun.type ?? "",
                parsedBodyPaths: [],
                headersPaths: [],
                queryPaths: [],
            };

            if (stepRun.type === "http") {
                const parsedBodySource =
                    stepRun.data_out?.["parsedBody"] ??
                    stepRun.data_out?.["body"] ??
                    sanitizeTopLevelDataOut(stepRun.data_out);

                current.parsedBodyPaths.push(...collectLeafPaths(parsedBodySource));
                current.headersPaths.push(...collectLeafPaths(stepRun.data_out?.["headers"]));
                current.queryPaths.push(...collectLeafPaths(stepRun.data_out?.["query"]));
            } else if (stepRun.type === "webhook") {
                const stepOutput = stepRun.step_id ? stepRun.data_out?.[stepRun.step_id] : undefined;
                const sanitized = sanitizeTopLevelDataOut(stepOutput);
                current.parsedBodyPaths.push(...collectLeafPaths(sanitized));
            } else if (stepRun.type === "scenario") {
                const sanitized = sanitizeTopLevelDataOut(stepRun.data_out);
                current.parsedBodyPaths.push(...collectLeafPaths(sanitized));
            }

            byStepId.set(stepRun.step_id, current);
        });

    const result = Array.from(byStepId.entries()).map(([stepId, value]) => ({
        stepId,
        stepType: value.stepType,
        parsedBodyPaths: normalizePathSet(value.parsedBodyPaths),
        headersPaths: normalizePathSet(value.headersPaths),
        queryPaths: normalizePathSet(value.queryPaths),
    }));

    return result;
};
